import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { environment } from '../../environments/environment';

interface NavigationItem {
  label: string;
  icon: string;
  route: string;
  exact: boolean;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive],
  template: `
    <aside class="sidebar" [class.collapsed]="collapsed" [class.mobile-open]="mobileOpen">
      <div class="brand-block">
        <div class="brand-mark"><img src="/assets/brand/company-logo.png" alt="CIAMCOM Central Scheduler" /></div>
        <div class="brand-copy" *ngIf="!collapsed">
          <span>CIAMCOM</span><strong>Central Scheduler</strong>
        </div>
        <button type="button" class="collapse-button" (click)="collapseToggle.emit()" aria-label="Toggle navigation">
          <i class="pi" [class.pi-angle-left]="!collapsed" [class.pi-angle-right]="collapsed"></i>
        </button>
      </div>

      <nav aria-label="Primary navigation">
        <p *ngIf="!collapsed">Workspace</p>
        <a *ngFor="let item of items" [routerLink]="item.route" routerLinkActive="active"
          [routerLinkActiveOptions]="{exact:item.exact}" (click)="navigate.emit()">
          <i [class]="item.icon"></i><span *ngIf="!collapsed">{{item.label}}</span>
        </a>
        <!-- Server-rendered Hangfire dashboard, not an Angular route: opened directly against the
             backend origin in a new tab, since it authenticates via its own cookie/OIDC login
             rather than the SPA's bearer token. -->
        <a [href]="hangfireUrl" target="_blank" rel="noopener noreferrer">
          <i class="pi pi-server"></i><span *ngIf="!collapsed">Background Jobs</span>
        </a>
      </nav>

      <div class="sidebar-note" *ngIf="!collapsed">
        Monitor sync services, queued work, and user changes from one workspace.
      </div>
    </aside>
  `,
  styles: [`
    :host{min-width:0;z-index:40}.sidebar{position:sticky;top:20px;height:calc(100vh - 40px);display:flex;flex-direction:column;padding:18px 14px;border:1px solid #dce4ef;border-radius:24px;background:#fff;box-shadow:0 18px 45px #1a2a440d;transition:width .18s ease,transform .18s ease}
    .brand-block{display:flex;align-items:center;gap:12px;min-height:62px}.brand-mark{width:58px;height:52px;display:grid;place-items:center;border:1px solid #e2e8f0;border-radius:14px;background:#fff;overflow:hidden;flex:none}.brand-mark img{width:100%;height:100%;object-fit:contain}.brand-copy{display:grid;min-width:0}.brand-copy span{color:#d51b48;font-size:.68rem;font-weight:800;letter-spacing:.16em}.brand-copy strong{color:#17233b;font-size:.98rem;white-space:nowrap}.collapse-button{margin-left:auto;width:36px;height:36px;border:0;border-radius:12px;background:#f2f5f9;color:#53627a;cursor:pointer}
    nav{display:grid;gap:6px;margin-top:24px}nav p{margin:0 10px 7px;color:#8a96a9;font-size:.7rem;font-weight:800;letter-spacing:.14em;text-transform:uppercase}nav a{min-height:48px;display:flex;align-items:center;gap:12px;padding:0 14px;border-radius:14px;color:#46546c;text-decoration:none;font-weight:700;transition:.15s ease}nav a:hover{background:#f2f5f9;color:#1a2740}nav a.active{background:#d7194a;color:#fff;box-shadow:0 10px 22px #d7194a2e}nav a i{width:20px;text-align:center}.collapsed .brand-block{flex-direction:column}.collapsed .collapse-button{margin-left:0}.collapsed nav a{justify-content:center;padding:0}
    .sidebar-note{margin-top:auto;padding:16px;border-radius:17px;background:#17233b;color:#dce5f3;font-size:.78rem;line-height:1.55}
    @media(max-width:1100px){:host{position:fixed;inset:0 auto 0 0;pointer-events:none}.sidebar,.sidebar.collapsed{position:relative;top:0;width:280px;height:100vh;border-radius:0;transform:translateX(-105%);pointer-events:auto}.sidebar.mobile-open{transform:translateX(0)}.collapsed .brand-block{flex-direction:row}.collapsed nav a{justify-content:flex-start;padding:0 14px}}
  `]
})
export class SidebarComponent {
  @Input() collapsed = false;
  @Input() mobileOpen = false;
  @Output() readonly collapseToggle = new EventEmitter<void>();
  @Output() readonly navigate = new EventEmitter<void>();

  readonly items: NavigationItem[] = [
    { label: 'Dashboard', icon: 'pi pi-home', route: '/dashboard', exact: true },
    { label: 'Services', icon: 'pi pi-sync', route: '/sync-services', exact: false },
    { label: 'Queue', icon: 'pi pi-list', route: '/queue', exact: false },
    { label: 'Runs', icon: 'pi pi-history', route: '/runs', exact: false },
    { label: 'User Imports', icon: 'pi pi-users', route: '/user-imports', exact: false },
  ];

  readonly hangfireUrl = `${environment.apiBaseUrl}/hangfire`;
}
